/* eslint-disable react/no-unknown-property */
import React from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Button, Icon, IconButton } from '@pypestream/design-system';

import {
  NodeColumns,
  TableNode,
  tableStore,
} from '../../../../stores/table-store';
import {
  ActionAssistAborted,
  useActionAssist,
  type ActionBriefInput,
} from '../../../../hooks/use-action-assist';
import { useBobcatFlag } from '../../../../hooks/use-bobcat-flag';
import { solutionAssistantStore } from '../../../../stores/solution-assistant-store';
import {
  DRAFT_COLUMN_LABELS,
  type DraftField,
  currentFieldText,
  draftColumnKind,
  draftFieldCommitText,
  draftFieldTag,
  knownVariables,
  normVar,
  orderDraftFields,
  splitList,
  summarizeSolution,
} from './action-assist-model';
import { TextExpander } from '../expandable-textarea';
import { buildDraftApplyUpdates } from './apply-draft';

import './ai-action-assist-panel.scss';

/**
 * Project Bobcat — AI Action Assist (`bobcat-ai-action-assist`).
 *
 * The prototype's `agent-ai-action-assistant.html`: opened from the node
 * panel's own head on an action node, it takes the node panel's place until
 * it is applied or closed. Assisted build: a brief (form) → Claude drafts the
 * node's fields → one review card per field, each kept or dropped → Apply
 * writes only the kept fields, as ONE undoable edit through the grid's own
 * commit path. Nothing is written before Apply. A drafted command that the
 * version does not have yet stays a name: its script is uploaded in Actions,
 * exactly as the prototype says.
 *
 * Questions about the solution moved to the header's solution assistant
 * (`bobcat-solution-assistant`); "Ask about this node" opens it.
 */

type Stage = 'form' | 'working' | 'review';

interface Brief {
  scriptName: string;
  purpose: string;
  parameterInput: string[];
  globals: string[];
  details: string;
  outputVariables: string[];
  outcomes: string;
}

interface NodeSession {
  brief: Brief;
}

/**
 * Briefs are PER NODE, and survive closing the panel for the rest
 * of the session: carrying one node's brief onto another is how a plausible
 * draft for the wrong node would happen.
 */
const SESSIONS = new Map<string, NodeSession>();

const EXAMPLES: Record<string, { label: string; purpose: string }> = {
  lookup: {
    label: 'Look up an order',
    purpose:
      'This script will look up the customer’s most recent order by order number and return the status and delivery date.',
  },
  reset: {
    label: 'Reset a variable',
    purpose:
      'This script will clear the variables the knowledge agent set, so the next turn starts from a clean context.',
  },
  route: {
    label: 'Route on an LLM result',
    purpose:
      'This script will read the classification the supervisor LLM returned and route the conversation to the node that handles it.',
  },
};

const seedSession = (row: TableNode): NodeSession => ({
  brief: {
    scriptName: String(row.command ?? ''),
    purpose: String(row.description ?? ''),
    parameterInput: splitList(String(row.nodeInput ?? '')).map(normVar),
    globals: [],
    details: '',
    outputVariables: (Array.isArray(row.output) ? row.output : [])
      .map((v) => normVar(String(v)))
      .filter(Boolean),
    outcomes: '',
  },
});

const sessionFor = (row: TableNode): NodeSession => {
  const existing = SESSIONS.get(row.id);
  if (existing) return existing;
  const fresh = seedSession(row);
  SESSIONS.set(row.id, fresh);
  return fresh;
};

/** A variable list as pills, with an inline picker over the solution's own
 *  variables. Typed names are normalised, not rejected. */
const VarPills: React.FC<{
  label: string;
  help: string;
  values: string[];
  known: string[];
  onChange: (next: string[]) => void;
  testId: string;
}> = ({ label, help, values, known, onChange, testId }) => {
  const [adding, setAdding] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const q = normVar(query);
  const suggestions = known
    .filter((v) => !values.includes(v) && (!q || v.includes(q)))
    .slice(0, 8);

  const add = (name: string) => {
    const n = normVar(name);
    if (n && !values.includes(n)) onChange([...values, n]);
    setQuery('');
    setAdding(false);
  };

  return (
    <div className="c-ai-assist__field" test-id={testId}>
      <p className="c-ai-assist__lbl">
        <span className="c-ai-assist__lbl-text">
          {label}
          <span title={help}>
            <Icon name="info" size="xsmall" />
          </span>
        </span>
      </p>
      <div className="c-ai-assist__pills">
        {values.map((v) => (
          <span key={v} className="c-ai-assist__var-pill">
            {v}
            <button
              type="button"
              className="c-ai-assist__var-x"
              aria-label={`Remove ${v}`}
              onClick={() => onChange(values.filter((x) => x !== v))}
            >
              ×
            </button>
          </span>
        ))}
        {adding ? (
          <span className="c-ai-assist__var-picker">
            <input
              autoFocus
              className="c-ai-assist__var-input"
              value={query}
              placeholder="VARIABLE_NAME"
              aria-label={`Add to ${label}`}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  add(query);
                }
                if (e.key === 'Escape') setAdding(false);
              }}
              onBlur={() => setTimeout(() => setAdding(false), 150)}
            />
            {suggestions.length > 0 && (
              <span className="c-ai-assist__var-menu" role="listbox">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className="c-ai-assist__var-option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      add(s);
                    }}
                  >
                    {s}
                  </button>
                ))}
              </span>
            )}
          </span>
        ) : (
          <button
            type="button"
            className="c-ai-assist__var-add"
            onClick={() => setAdding(true)}
          >
            + Add variable
          </button>
        )}
      </div>
    </div>
  );
};

const RoutesView: React.FC<{ value: string }> = ({ value }) => (
  <div className="c-ai-assist__routes">
    {value
      .split('|')
      .filter(Boolean)
      .map((pair) => {
        const [out, node] = pair.split('~');
        return (
          <div key={pair} className="c-ai-assist__route">
            <span className="c-ai-assist__route-out">{out}</span>
            <span className="c-ai-assist__route-arrow">→</span>
            <span className="c-ai-assist__route-node">
              {node || '(no target)'}
            </span>
          </div>
        );
      })}
  </div>
);

const FieldValue: React.FC<{ field: DraftField }> = ({ field }) => {
  const kind = draftColumnKind(field.column);
  if (kind === 'pills') {
    return (
      <div className="c-ai-assist__pills">
        {splitList(field.value).map((v) => (
          <span key={v} className="c-ai-assist__var-pill">
            {v}
          </span>
        ))}
      </div>
    );
  }
  if (kind === 'map') return <RoutesView value={field.value} />;
  if (kind === 'json') {
    return (
      <pre className="c-ai-assist__code">{draftFieldCommitText(field)}</pre>
    );
  }
  return <div className="c-ai-assist__val">{field.value}</div>;
};

const TAG_TEXT = { new: 'New', overwrite: 'Replaces', updated: 'Updated' };

/** Build with AI is offered only on action nodes, where the prototype
 *  offers it. */
export const isActionAssistNode = (row: TableNode | undefined): boolean =>
  !!row && String(row.nodeType ?? '').toLowerCase() === 'a';

export const AiActionAssistPanel: React.FC<{
  rowId: string;
  commands: string[];
  onClose: () => void;
  /** After Apply: back to where it was opened from. */
  onApplied: () => void;
  /** Inside the Ask AI dock: a back button instead of "Ask about this node". */
  embedded?: boolean;
}> = observer(({ rowId, commands, onClose, onApplied, embedded }) => {
  const row = tableStore.getRowById(rowId);
  const { draft: requestDraft, stop } = useActionAssist();
  const solutionAssistantEnabled =
    useBobcatFlag('solutionAssistant') === 'allowed';

  const [stage, setStage] = React.useState<Stage>('form');
  const [brief, setBriefState] = React.useState<Brief>(() =>
    row ? sessionFor(row).brief : seedSession({} as TableNode).brief
  );
  const [fields, setFields] = React.useState<DraftField[]>([]);
  const [keep, setKeep] = React.useState<Record<string, boolean>>({});
  const [summary, setSummary] = React.useState('');
  const [turns, setTurns] = React.useState<{ who: string; text: string }[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [refineText, setRefineText] = React.useState('');

  const rows = tableStore.getAllSortedRows();
  // Once per node, not per keystroke: a 500-node solution makes this real work.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const known = React.useMemo(() => knownVariables(rows), [rowId]);

  const setBrief = (patch: Partial<Brief>) =>
    setBriefState((prev) => {
      const next = { ...prev, ...patch };
      if (row) sessionFor(row).brief = next;
      return next;
    });

  if (!row) return null;

  const nodeNumber = String(row.nodeNumber ?? '');
  const context = () =>
    summarizeSolution(
      tableStore.getAllSortedRows().map((r) => toJS(r)),
      rowId
    );
  const briefInput = (): ActionBriefInput => ({ ...brief });

  const runDraft = async (refine?: string) => {
    setError(null);
    setBusy(true);
    if (!refine) setStage('working');
    try {
      const result = await requestDraft({
        brief: briefInput(),
        context: context(),
        nodeNumber,
        commands,
        ...(refine ? { previous: fields, request: refine } : {}),
      });
      const ordered = orderDraftFields(result.fields);
      setFields(ordered);
      setKeep((prev) =>
        Object.fromEntries(
          ordered.map((f) => [
            f.column,
            refine ? (prev[f.column] ?? true) : true,
          ])
        )
      );
      setSummary(result.summary);
      if (refine) {
        setTurns((t) => [
          ...t,
          { who: 'AI', text: result.reply || 'Updated the draft.' },
        ]);
      } else {
        setTurns([]);
      }
      setStage('review');
    } catch (err) {
      if (err instanceof ActionAssistAborted) {
        if (!refine) setStage('form');
      } else {
        setError(err instanceof Error ? err.message : String(err));
        if (!refine) setStage('form');
      }
    } finally {
      setBusy(false);
    }
  };

  const sendRefine = () => {
    const text = refineText.trim();
    if (!text || busy) return;
    setTurns((t) => [...t, { who: 'You', text }]);
    setRefineText('');
    void runDraft(text);
  };

  const apply = () => {
    const kept = fields.filter((f) => keep[f.column]);
    if (!kept.length) return;
    const updates = buildDraftApplyUpdates(fields, keep, (column) =>
      tableStore.isJsonColumn(rowId, column as keyof TableNode)
    );
    // One undo entry for the whole apply, through the same write the grid's
    // own edits use — not a second path.
    tableStore.recordRowEdit(
      rowId,
      'Apply AI Action Assist draft',
      () => {
        tableStore.updateRow({
          rowId,
          updates,
          skipValidationRules: false,
          saveImmediately: true,
        });
      },
      NodeColumns.command
    );
    setFields([]);
    setStage('form');
    onApplied();
  };

  const keptCount = fields.filter((f) => keep[f.column]).length;

  const renderForm = () => (
    <>
      <div className="c-ai-assist__body" test-id="ps-ai-assist-form">
        <div className="c-ai-assist__field">
          <p className="c-ai-assist__lbl">
            <span className="c-ai-assist__lbl-text">Script Name</span>
          </p>
          <input
            className="c-ai-assist__input"
            value={brief.scriptName}
            placeholder="e.g. AddFundingSource"
            aria-label="Script Name"
            test-id="ps-ai-assist-name"
            onChange={(e) => setBrief({ scriptName: e.target.value })}
          />
        </div>

        <div className="c-ai-assist__field">
          <p className="c-ai-assist__lbl">
            <span className="c-ai-assist__lbl-text">
              Script Purpose
              <span title="One or two sentences. This becomes the node’s Description and steers the command choice.">
                <Icon name="info" size="xsmall" />
              </span>
            </span>
            <TextExpander
              buttonClassName="c-ai-assist__expand"
              labelText="Script Purpose"
              value={brief.purpose}
              testId="ps-ai-assist-purpose"
              onChange={(next) => setBrief({ purpose: next })}
            />
          </p>
          <textarea
            className="c-ai-assist__textarea"
            rows={3}
            value={brief.purpose}
            placeholder="This script will…"
            aria-label="Script Purpose"
            test-id="ps-ai-assist-purpose"
            onChange={(e) => setBrief({ purpose: e.target.value })}
          />
          <div className="c-ai-assist__chip-row">
            {Object.entries(EXAMPLES).map(([key, ex]) => (
              <button
                key={key}
                type="button"
                className="c-ai-assist__chip"
                onClick={() => setBrief({ purpose: ex.purpose })}
              >
                {ex.label}
              </button>
            ))}
          </div>
        </div>

        <p className="c-ai-assist__sect">Inputs</p>
        <VarPills
          label="Parameter input"
          help="Variables this node reads from the conversation. They become Node Input and the inputs block of Parameter Input."
          values={brief.parameterInput}
          known={known}
          onChange={(parameterInput) => setBrief({ parameterInput })}
          testId="ps-ai-assist-param-in"
        />
        <VarPills
          label="Global variables"
          help="Solution-wide values the script may reference."
          values={brief.globals}
          known={known}
          onChange={(globals) => setBrief({ globals })}
          testId="ps-ai-assist-globals"
        />

        <div className="c-ai-assist__field">
          <p className="c-ai-assist__lbl">
            <span className="c-ai-assist__lbl-text">
              Details &amp; Logic
              <span title="Headers, endpoints, request shape — anything the draft could not work out from the purpose alone.">
                <Icon name="info" size="xsmall" />
              </span>
            </span>
            <TextExpander
              buttonClassName="c-ai-assist__expand"
              labelText="Details & Logic"
              value={brief.details}
              testId="ps-ai-assist-details"
              onChange={(next) => setBrief({ details: next })}
            />
          </p>
          <textarea
            className="c-ai-assist__textarea c-ai-assist__textarea--tall"
            rows={6}
            value={brief.details}
            aria-label="Details and Logic"
            onChange={(e) => setBrief({ details: e.target.value })}
          />
        </div>

        <p className="c-ai-assist__sect">Outputs</p>
        <VarPills
          label="Output variables"
          help="What this node returns. These become the Output and Decision Variable columns."
          values={brief.outputVariables}
          known={known}
          onChange={(outputVariables) => setBrief({ outputVariables })}
          testId="ps-ai-assist-outputs"
        />
        <div className="c-ai-assist__field">
          <p className="c-ai-assist__lbl">
            <span className="c-ai-assist__lbl-text">
              What each outcome means
              <span title="Spell out every value the outputs can take. This is what the What Next? routing map is built from.">
                <Icon name="info" size="xsmall" />
              </span>
            </span>
            <TextExpander
              buttonClassName="c-ai-assist__expand"
              labelText="What each outcome means"
              value={brief.outcomes}
              testId="ps-ai-assist-outcomes"
              onChange={(next) => setBrief({ outcomes: next })}
            />
          </p>
          <textarea
            className="c-ai-assist__textarea c-ai-assist__textarea--tall"
            rows={5}
            value={brief.outcomes}
            aria-label="What each outcome means"
            onChange={(e) => setBrief({ outcomes: e.target.value })}
          />
        </div>

        {error && (
          <p
            className="c-ai-assist__error"
            role="alert"
            test-id="ps-ai-assist-error"
          >
            {error}
          </p>
        )}
      </div>
      <div className="c-ai-assist__foot">
        <span className="c-ai-assist__foot-meta">
          Nothing is written until you apply it.
        </span>
        <Button
          variant="ghost"
          size="small"
          onClick={() => {
            if (!row) return;
            const fresh = seedSession(row).brief;
            setBrief({
              ...fresh,
              scriptName: '',
              purpose: '',
              parameterInput: [],
              outputVariables: [],
            });
            setError(null);
          }}
        >
          Clear
        </Button>
        <Button
          size="small"
          test-id="ps-ai-assist-create"
          disabled={
            !(
              brief.scriptName.trim() ||
              brief.purpose.trim() ||
              brief.details.trim()
            )
          }
          onClick={() => void runDraft()}
        >
          Create
        </Button>
      </div>
    </>
  );

  const renderWorking = () => (
    <>
      <div className="c-ai-assist__body" test-id="ps-ai-assist-working">
        <div className="c-ai-assist__work-step c-ai-assist__work-step--done">
          <span className="c-ai-assist__work-dot">✓</span>
          <span>
            Reading solution context
            <i>
              {rows.filter((r) => String(r.nodeType) !== 'g').length} nodes,{' '}
              {known.length} variables
            </i>
          </span>
        </div>
        <div className="c-ai-assist__work-step c-ai-assist__work-step--doing">
          <span className="c-ai-assist__work-dot" />
          <span>
            Drafting the command and parameters
            <i>Checking every What Next? target against this solution</i>
          </span>
        </div>
        <div className="c-ai-assist__skeletons">
          <div className="c-ai-assist__skeleton" style={{ width: '64%' }} />
          <div className="c-ai-assist__skeleton" style={{ width: '92%' }} />
          <div className="c-ai-assist__skeleton" style={{ width: '78%' }} />
          <div className="c-ai-assist__skeleton" style={{ width: '40%' }} />
        </div>
      </div>
      <div className="c-ai-assist__foot">
        <span className="c-ai-assist__foot-meta">
          Usually takes a few seconds.
        </span>
        <Button variant="ghost" size="small" onClick={stop}>
          Stop
        </Button>
      </div>
    </>
  );

  const renderReview = () => (
    <>
      <div className="c-ai-assist__body" test-id="ps-ai-assist-review">
        <div className="c-ai-assist__summary">
          <Icon name="stars" size="xsmall" />
          <span>
            Drafted {fields.length} field{fields.length === 1 ? '' : 's'} for
            node {nodeNumber}. {summary}
          </span>
        </div>
        {fields.map((f) => {
          const tag = draftFieldTag(toJS(row), f);
          const prev = currentFieldText(toJS(row), f.column);
          const on = !!keep[f.column];
          return (
            <div
              key={f.column}
              className={`c-ai-assist__gen${on ? '' : ' c-ai-assist__gen--off'}${
                tag === 'updated' ? ' c-ai-assist__gen--updated' : ''
              }`}
              test-id={`ps-ai-assist-card-${f.column}`}
            >
              <div className="c-ai-assist__gen-head">
                <span className="c-ai-assist__gen-label">
                  {DRAFT_COLUMN_LABELS[f.column]}
                </span>
                <span
                  className={`c-ai-assist__gen-tag c-ai-assist__gen-tag--${tag}`}
                >
                  {TAG_TEXT[tag]}
                </span>
                <label className="c-ai-assist__gen-keep">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) =>
                      setKeep((k) => ({ ...k, [f.column]: e.target.checked }))
                    }
                  />{' '}
                  Keep
                </label>
              </div>
              <div className="c-ai-assist__gen-body">
                <FieldValue field={f} />
                {prev && tag !== 'new' && (
                  <div className="c-ai-assist__gen-prev">
                    Currently <s>{prev.slice(0, 90)}</s>
                  </div>
                )}
                {f.why && (
                  <p className="c-ai-assist__gen-why">
                    <Icon name="info" size="xsmall" />
                    {f.why}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="c-ai-assist__refine">
        {turns.length > 0 && (
          <div className="c-ai-assist__refine-turns">
            {turns.map((t, i) => (
              <div key={i} className="c-ai-assist__refine-turn">
                <b>{t.who}</b>
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        )}
        {error && (
          <p className="c-ai-assist__error" role="alert">
            {error}
          </p>
        )}
        <div className="c-ai-assist__refine-box">
          <textarea
            rows={1}
            value={refineText}
            disabled={busy}
            placeholder={
              busy
                ? 'Updating the draft…'
                : 'Ask for a change — e.g. send the error case to 99990'
            }
            aria-label="Ask for a change"
            onChange={(e) => setRefineText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendRefine();
              }
            }}
          />
          <button
            type="button"
            className="c-ai-assist__send"
            aria-label="Send"
            disabled={busy || !refineText.trim()}
            onClick={sendRefine}
          >
            ➤
          </button>
        </div>
      </div>
      <div className="c-ai-assist__foot">
        <span className="c-ai-assist__foot-meta">
          {keptCount} of {fields.length} fields selected
        </span>
        <Button variant="ghost" size="small" onClick={() => setStage('form')}>
          Edit inputs
        </Button>
        <Button
          size="small"
          test-id="ps-ai-assist-apply"
          disabled={!keptCount || busy}
          onClick={apply}
        >
          Apply to node
        </Button>
      </div>
    </>
  );

  return (
    <div className="c-ai-assist" test-id="ps-ai-assist-panel">
      <div className="c-ai-assist__head">
        {embedded && (
          <IconButton
            name="chevron-left"
            size="small"
            test-id="ps-ai-assist-back"
            aria-label="Back to Ask AI"
            onClick={() => {
              stop();
              onClose();
            }}
          />
        )}
        <span className="c-ai-assist__head-title">
          <Icon name="stars" size="small" />
          {embedded ? 'Build with AI' : 'AI Action Assist'}
        </span>
        <span className="c-ai-assist__head-node">
          {nodeNumber} {String(row.nodeName ?? '')}
        </span>
        {!embedded && solutionAssistantEnabled && (
          <button
            type="button"
            className="c-ai-assist__act"
            test-id="ps-ai-assist-ask-about-node"
            onClick={() => solutionAssistantStore.openDock()}
          >
            Ask about this node
          </button>
        )}
        {!embedded && (
          <IconButton
            name="close"
            size="small"
            test-id="ps-ai-assist-close"
            aria-label="Back to the node panel"
            onClick={() => {
              stop();
              onClose();
            }}
          />
        )}
      </div>
      {stage === 'working'
        ? renderWorking()
        : stage === 'review'
          ? renderReview()
          : renderForm()}
    </div>
  );
});
